"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const hammerjs_1 = __importDefault(require("hammerjs"));
const utils_1 = require("./utils");
const ViewPortCamera_1 = require("./ViewPortCamera");
/**
 * The `ViewPort` represents a "view" into a virtual space, that is not
 * tied to the available screen space or HTML elements. Because of this, it is
 * infinite, but it also uses its own "units" (virtual space pixels).
 *
 * You can think of the view port as describing what rectangular portion of the
 * virtual space (from top left to bottom right) should be visible inside the
 * bounds of containing HTML element where the virtual space is being rendered.
 *
 * Please see the [ Guide ](../../Guide.md) for more details.
 */
class ViewPort {
    constructor(containerDiv, options) {
        var _a;
        this.getPressCoordinatesFromEvent = (e) => {
            let clientX;
            let clientY;
            if (utils_1.isMouseEvent(e)) {
                clientX = e.clientX;
                clientY = e.clientY;
            }
            else {
                clientX = e.changedTouches[0].clientX;
                clientY = e.changedTouches[0].clientY;
            }
            const x = clientX / this.zoomFactor + this.left;
            const y = clientY / this.zoomFactor + this.top;
            const clientBoundingRect = this.containerDiv.getBoundingClientRect();
            const containerX = clientX - clientBoundingRect.left;
            const containerY = clientY - clientBoundingRect.top;
            return { x, y, clientX, clientY, containerX, containerY };
        };
        this.handleContextMenu = (e) => {
            var _a, _b, _c, _d, _e;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleContextMenu`);
            }
            if ((_b = this.options) === null || _b === void 0 ? void 0 : _b.treatRightClickAsPan) {
                e.preventDefault();
            }
            else {
                (_e = (_c = this.options) === null || _c === void 0 ? void 0 : (_d = _c).onContextMenu) === null || _e === void 0 ? void 0 : _e.call(_d, e, this.getPressCoordinatesFromEvent(e));
            }
        };
        this.handleGestureStartForDesktopSafari = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleGestureStartForDesktopSafari`);
            }
            e.preventDefault();
            this.currentDesktopSafariGestureState = {
                startingCenterX: e.clientX,
                startingCenterY: e.clientY,
                scale: e.scale,
            };
        };
        this.handleGestureChangeForDesktopSafari = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleGestureChangeForDesktopSafari`);
            }
            e.preventDefault();
            if (!this.currentDesktopSafariGestureState) {
                return;
            }
            const clientBoundingRect = this.containerDiv.getBoundingClientRect();
            const pointerContainerX = this.currentDesktopSafariGestureState.startingCenterX - clientBoundingRect.left;
            const pointerContainerY = this.currentDesktopSafariGestureState.startingCenterY - clientBoundingRect.top;
            // Divide by 2 so it feels less fast
            const dZoom = (e.scale - this.currentDesktopSafariGestureState.scale) / 2;
            this.currentDesktopSafariGestureState.scale = e.scale;
            this.camera.moveByInClientSpace(0, 0, dZoom, pointerContainerX, pointerContainerY);
        };
        this.handleGestureEndForDesktopSafari = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleGestureEndForDesktopSafari`);
            }
            e.preventDefault();
            this.currentDesktopSafariGestureState = undefined;
        };
        this.handleHammerPanStart = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPanStart`);
            }
            this.currentHammerGestureState = undefined;
        };
        this.handleHammerPanMove = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPanMove`);
            }
            if (this.currentHammerGestureState === undefined) {
                this.currentHammerGestureState = {
                    deltaX: 0,
                    deltaY: 0,
                    scale: undefined,
                };
            }
            const dx = this.currentHammerGestureState.deltaX - e.deltaX;
            const dy = this.currentHammerGestureState.deltaY - e.deltaY;
            this.currentHammerGestureState.deltaX = e.deltaX;
            this.currentHammerGestureState.deltaY = e.deltaY;
            if (this.pressHandlingMode) {
                return;
            }
            const clientBoundingRect = this.containerDiv.getBoundingClientRect();
            const pointerContainerX = e.center.x - clientBoundingRect.left;
            const pointerContainerY = e.center.y - clientBoundingRect.top;
            this.camera.moveByInClientSpace(dx, dy, 0, pointerContainerX, pointerContainerY);
        };
        this.handleHammerPanEnd = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPanEnd (` + e.velocityX + ',' + e.velocityY + ')');
            }
            if (!this.pressHandlingMode) {
                // Negative one because the direction of the pointer is the opposite of
                // the direction we are moving the viewport. Multiplying by 20 makes it
                // feel more normal.
                this.camera.moveWithDecelerationInClientSpace(-1 * e.velocityX * 20, -1 * e.velocityY * 20);
            }
            this.currentHammerGestureState = undefined;
        };
        this.handleHammerPanCancel = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPanCancel`);
            }
            this.currentHammerGestureState = undefined;
        };
        this.handleHammerPinchStart = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPinchStart`);
            }
            this.currentHammerGestureState = undefined;
        };
        this.handleHammerPinchMove = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPinchMove`);
            }
            if (this.currentHammerGestureState === undefined) {
                this.currentHammerGestureState = {
                    deltaX: e.deltaX,
                    deltaY: e.deltaY,
                    scale: e.scale,
                };
            }
            const dx = this.currentHammerGestureState.deltaX - e.deltaX;
            const dy = this.currentHammerGestureState.deltaY - e.deltaY;
            // Divide by 2 so it feels less fast
            const dZoom = (e.scale - (this.currentHammerGestureState.scale || e.scale)) / 2;
            this.currentHammerGestureState.deltaX = e.deltaX;
            this.currentHammerGestureState.deltaY = e.deltaY;
            this.currentHammerGestureState.scale = e.scale;
            const clientBoundingRect = this.containerDiv.getBoundingClientRect();
            const pointerContainerX = e.center.x - clientBoundingRect.left;
            const pointerContainerY = e.center.y - clientBoundingRect.top;
            this.camera.moveByInClientSpace(dx, dy, dZoom, pointerContainerX, pointerContainerY);
        };
        this.handleHammerPinchEnd = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPinchEnd`);
            }
            if (!this.pressHandlingMode) {
                // Negative one because the direction of the pointer is the opposite of
                // the direction we are moving the viewport. Multiplying by 20 makes it
                // feel more normal.
                this.camera.moveWithDecelerationInClientSpace(-1 * e.velocityX * 20, -1 * e.velocityY * 20);
            }
            this.currentHammerGestureState = undefined;
        };
        this.handleHammerPinchCancel = (e) => {
            var _a;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleHammerPinchCancel`);
            }
            this.currentHammerGestureState = undefined;
        };
        this.handleMouseDown = (e) => {
            var _a, _b, _c, _d, _e;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleMouseDown`);
            }
            // e.buttons === 1 means the left/primary button is pressed and ONLY that
            // e.buttons === 2
            const isLeftOnly = e.buttons === 1;
            const isRightOnly = e.buttons === 2;
            const shouldHandleAsPan = isLeftOnly || (((_b = this.options) === null || _b === void 0 ? void 0 : _b.treatRightClickAsPan) && isRightOnly);
            if (!shouldHandleAsPan) {
                return;
            }
            if (isLeftOnly) {
                this.pressHandlingMode = (_e = (_c = this.options) === null || _c === void 0 ? void 0 : (_d = _c).onPressStart) === null || _e === void 0 ? void 0 : _e.call(_d, e, this.getPressCoordinatesFromEvent(e));
                if (this.pressHandlingMode === 'capture') {
                    e.preventDefault();
                }
            }
            else if (isRightOnly) {
                e.preventDefault();
                // Sadly hammer.js doesn't give us an option to treat right clicks as
                // pans so we have to make this work ourselves.
                this.pressHandlingMode = 'rightclickpan';
                this.rightClickPanState = {
                    lastClientX: e.clientX,
                    lastClientY: e.clientY,
                    velocityX: 0,
                    velocityY: 0,
                };
            }
        };
        this.handleMouseMove = (e) => {
            var _a, _b, _c, _d, _e;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleMouseMove (pressHandlingMode: ${this.pressHandlingMode})`);
            }
            if (this.pressHandlingMode === 'capture') {
                if (e.buttons !== 1) {
                    // Intentionally don't do anything... maybe cancel here?
                }
                else {
                    if ((_b = this.options) === null || _b === void 0 ? void 0 : _b.onPressMove) {
                        if (this.options.onPressMove(e, this.getPressCoordinatesFromEvent(e)) === 'release') {
                            this.pressHandlingMode = undefined;
                        }
                    }
                }
            }
            else if (this.pressHandlingMode === 'rightclickpan') {
                if (e.buttons !== 2 || !this.rightClickPanState) {
                    // Intentionally don't do anything... maybe reset this.pressHandlingMode?
                }
                else {
                    const dx = this.rightClickPanState.lastClientX - e.clientX;
                    const dy = this.rightClickPanState.lastClientY - e.clientY;
                    this.rightClickPanState.lastClientX = e.clientX;
                    this.rightClickPanState.lastClientY = e.clientY;
                    this.rightClickPanState.velocityX = dx;
                    this.rightClickPanState.velocityY = dy;
                    const clientBoundingRect = this.containerDiv.getBoundingClientRect();
                    const pointerContainerX = e.clientX - clientBoundingRect.left;
                    const pointerContainerY = e.clientY - clientBoundingRect.top;
                    this.camera.moveByInClientSpace(dx, dy, 0, pointerContainerX, pointerContainerY);
                }
            }
            else if (e.buttons === 0) {
                (_e = (_c = this.options) === null || _c === void 0 ? void 0 : (_d = _c).onHover) === null || _e === void 0 ? void 0 : _e.call(_d, e, this.getPressCoordinatesFromEvent(e));
            }
        };
        this.handleMouseUp = (e) => {
            var _a, _b, _c;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleMouseUp`);
            }
            if (this.pressHandlingMode === 'capture' && ((_b = this.options) === null || _b === void 0 ? void 0 : _b.onPressEnd)) {
                (_c = this.options) === null || _c === void 0 ? void 0 : _c.onPressEnd(e, this.getPressCoordinatesFromEvent(e));
            }
            if (this.pressHandlingMode === 'rightclickpan' && this.rightClickPanState) {
                const dx = this.rightClickPanState.velocityX;
                const dy = this.rightClickPanState.velocityY;
                this.camera.moveWithDecelerationInClientSpace(dx, dy);
            }
            this.pressHandlingMode = undefined;
            this.rightClickPanState = undefined;
        };
        this.handleTouchStart = (e) => {
            var _a, _b, _c, _d, _e, _f;
            if (e.touches.length !== 1) {
                if (this.pressHandlingMode) {
                    this.pressHandlingMode = undefined;
                    (_c = (_a = this.options) === null || _a === void 0 ? void 0 : (_b = _a).onPressCancel) === null || _c === void 0 ? void 0 : _c.call(_b, e);
                    return;
                }
            }
            this.pressHandlingMode = (_f = (_d = this.options) === null || _d === void 0 ? void 0 : (_e = _d).onPressStart) === null || _f === void 0 ? void 0 : _f.call(_e, e, this.getPressCoordinatesFromEvent(e));
            if (this.pressHandlingMode === 'capture') {
                e.preventDefault();
            }
        };
        this.handleTouchMove = (e) => {
            var _a, _b, _c;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleTouchMove`);
            }
            if (e.touches.length === 1) {
                if (this.pressHandlingMode === 'capture' && ((_b = this.options) === null || _b === void 0 ? void 0 : _b.onPressMove)) {
                    if (((_c = this.options) === null || _c === void 0 ? void 0 : _c.onPressMove(e, this.getPressCoordinatesFromEvent(e))) === 'release') {
                        this.pressHandlingMode = undefined;
                    }
                }
            }
        };
        this.handleTouchEnd = (e) => {
            var _a, _b, _c;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleTouchEnd`);
            }
            if (e.touches.length === 0 && e.changedTouches.length === 1) {
                if (this.pressHandlingMode === 'capture' && ((_b = this.options) === null || _b === void 0 ? void 0 : _b.onPressEnd)) {
                    (_c = this.options) === null || _c === void 0 ? void 0 : _c.onPressEnd(e, this.getPressCoordinatesFromEvent(e));
                }
            }
        };
        this.handleWheel = (e) => {
            var _a, _b;
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.debugEvents) {
                console.log(`ViewPort:handleWheel`, e);
            }
            e.preventDefault();
            let isPrimarilyZoom = true;
            if ((_b = this.options) === null || _b === void 0 ? void 0 : _b.treatTwoFingerTrackPadGesturesLikeTouch) {
                // For whatever reason, desktop browsers send pinch gestures with ctrlKey set to true.
                isPrimarilyZoom = e.ctrlKey;
            }
            if (isPrimarilyZoom) {
                let scale = e.ctrlKey ? 5 : 1; // This feels more right...
                switch (e.deltaMode) {
                    case 1: // DOM_DELTA_LINE
                        scale = 7.15625; // Line height total guesstimate from `to-px` (looking up their const for 'ex')
                        break;
                    case 2: // DOM_DELTA_PAGE
                        scale = window.innerHeight;
                        break;
                }
                const clientBoundingRect = this.containerDiv.getBoundingClientRect();
                const pointerContainerX = e.clientX - clientBoundingRect.left;
                const pointerContainerY = e.clientY - clientBoundingRect.top;
                const dy = e.deltaY * scale;
                const dZoom = ((-1 * dy) / this.containerHeight) * this.zoomFactor;
                // Vertical scroll is doing to be interpreted by us as changing z
                this.camera.moveByInClientSpace(0, 0, dZoom, pointerContainerX, pointerContainerY);
            }
            else {
                this.camera.moveByInClientSpace(e.deltaX / 2, e.deltaY / 2, 0);
            }
        };
        this.containerDiv = containerDiv;
        this.options = options;
        // Default values
        this.left = 0;
        this.top = 0;
        this.centerX = 0;
        this.centerY = 0;
        this.width = 0;
        this.height = 0;
        this.zoomFactor = 1;
        this.containerWidth = 0;
        this.containerHeight = 0;
        this.pressHandlingMode = undefined;
        // Bind methods JIC
        // tslint:disable-next-line: unnecessary-bind
        this.setBounds = this.setBounds.bind(this);
        this.updateContainerSize = this.updateContainerSize.bind(this);
        this.translateClientXYCoordinatesToVirtualSpace = this.translateClientXYCoordinatesToVirtualSpace.bind(this);
        this.translateClientRectToVirtualSpace = this.translateClientRectToVirtualSpace.bind(this);
        // Set the div's styles
        this.containerDiv.style.overflow = 'hidden';
        this.containerDiv.style.padding = '0';
        // Setup other stuff
        this.camera = new ViewPortCamera_1.ViewPortCamera(this, (_a = this.options) === null || _a === void 0 ? void 0 : _a.onUpdated);
        // Tell the camera about our div size this has to be after we change the style above...
        this.updateContainerSize();
        // Add event listeners
        // We use hammer for handling pinches and panning, and our own listeners for
        // everything else, including taps.
        this.containerDiv.addEventListener('mousedown', this.handleMouseDown);
        this.containerDiv.addEventListener('mousemove', this.handleMouseMove);
        // Doing this on window to catch it if it goes outside the window
        window.addEventListener('mouseup', this.handleMouseUp);
        this.containerDiv.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        this.containerDiv.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        this.containerDiv.addEventListener('touchend', this.handleTouchEnd);
        this.containerDiv.addEventListener('contextmenu', this.handleContextMenu);
        // There is no good way to detect whether an individual element is
        // resized. We can only do that at the window level. There are some
        // techniques for tracking element sizes, and we provide an OPTIONAL
        // polling based technique. But since watching for window resizes WILL
        // work for many use cases we do that here, and it shouldn't interfere
        // with any more specific techniques.
        window.addEventListener('resize', this.updateContainerSize);
        this.containerDiv.addEventListener('wheel', this.handleWheel, { passive: false });
        if (utils_1.browserIsSafariDesktop) {
            this.containerDiv.addEventListener('gesturestart', this.handleGestureStartForDesktopSafari);
            this.containerDiv.addEventListener('gesturechange', this.handleGestureChangeForDesktopSafari);
            this.containerDiv.addEventListener('gestureend', this.handleGestureEndForDesktopSafari);
        }
        // Set up the pan-zoom library
        // this.panZoomControl = panzoom(this.containerDiv, this.handlePanZoomEvent);
        this.hammer = new hammerjs_1.default(this.containerDiv, {});
        // Press and tap almost do what we want, but not quite. See README.md for
        // more info.
        this.hammer.remove('press');
        this.hammer.remove('tap');
        this.hammer.get('pinch').set({ enable: true });
        this.hammer.get('pan').set({ threshold: 0, direction: hammerjs_1.default.DIRECTION_ALL });
        this.hammer.on('panstart', this.handleHammerPanStart);
        this.hammer.on('panmove', this.handleHammerPanMove);
        this.hammer.on('panend', this.handleHammerPanEnd);
        this.hammer.on('pancancel', this.handleHammerPanCancel);
        this.hammer.on('pinchstart', this.handleHammerPinchStart);
        this.hammer.on('pinchmove', this.handleHammerPinchMove);
        this.hammer.on('pinchend', this.handleHammerPinchEnd);
        this.hammer.on('pinchcancel', this.handleHammerPinchCancel);
    }
    /**
     * Called this to detach all event listeners that the `ViewPort` sets up.
     * After this is called no further updates will happen.
     */
    destroy() {
        this.camera.destroy();
        this.containerDiv.removeEventListener('mousedown', this.handleMouseDown);
        this.containerDiv.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        this.containerDiv.removeEventListener('touchstart', this.handleTouchStart);
        this.containerDiv.removeEventListener('touchmove', this.handleTouchMove);
        this.containerDiv.removeEventListener('touchend', this.handleTouchEnd);
        this.containerDiv.removeEventListener('contextmenu', this.handleContextMenu);
        window.removeEventListener('resize', this.updateContainerSize);
        this.containerDiv.removeEventListener('wheel', this.handleWheel);
        if (utils_1.browserIsSafariDesktop) {
            // We have to handle the pinch gesture manually on desktop Safari
            this.containerDiv.removeEventListener('gesturestart', this.handleGestureStartForDesktopSafari);
            this.containerDiv.removeEventListener('gesturechange', this.handleGestureChangeForDesktopSafari);
            this.containerDiv.removeEventListener('gestureend', this.handleGestureEndForDesktopSafari);
        }
        this.hammer.destroy();
    }
    /**
     * Constrain the virtual space so the user can not pan beyond, and the camera
     * cannot show anything beyond, the provided min/max values for x, y, and the
     * zoom factor.
     */
    setBounds(bounds) {
        // The `camera` is really a `ViewPortCamera`, we just declare the field as
        // `ViewPortCameraInterface` to hide the `setBounds` method on it.
        this.camera.setBounds(bounds);
    }
    translateClientXYCoordinatesToVirtualSpace(x, y) {
        return {
            x: x / this.zoomFactor + this.left,
            y: y / this.zoomFactor + this.top,
        };
    }
    translateClientRectToVirtualSpace(rectOrElement) {
        if (!rectOrElement.getBoundingClientRect) {
            const rect = rectOrElement;
            return {
                bottom: rect.bottom / this.zoomFactor + this.top,
                height: rect.height / this.zoomFactor,
                left: rect.left / this.zoomFactor + this.left,
                right: rect.right / this.zoomFactor + this.left,
                top: rect.top / this.zoomFactor + this.top,
                width: rect.width / this.zoomFactor,
            };
        }
        else {
            const element = rectOrElement;
            return this.translateClientRectToVirtualSpace(element.getBoundingClientRect());
        }
    }
    /**
     * This should be used when the div is resized. By default resizes due to the
     * window itself resizing will be automatically handled, but any other
     * resizes won't be handled (since there isn't a good way to get notified
     * when the div resizes.
     *
     * If you are getting access to the `ViewPort` via `Space` or
     * `SpaceContext` you should not call this method directly and should
     * instead call the `Space.updateSize` method.
     */
    updateContainerSize() {
        const clientBoundingRect = this.containerDiv.getBoundingClientRect();
        const { width, height } = clientBoundingRect;
        this.camera.handleContainerSizeChanged(width, height);
    }
}
exports.ViewPort = ViewPort;
//# sourceMappingURL=ViewPort.js.map